/* ============================================================
   OBEYTHECULTURE  CATALOG (Liquid Integration)
   ------------------------------------------------------------
   Auto-behaviour:
      Reads existing .blu-card elements generated by Shopify Liquid.
      Wires .blu-pill filter chips to live-filter the grid via display:none.
      Hover image-swap on cards.
      Quick-add "+" button delegation.
   ============================================================ */
(() => {
  'use strict';

  const grids = [];                 // every .blu-grid rendered on the page
  const gridState = new WeakMap();  // grid -> { category, collection, sort }

  function registerGrid(grid) {
    grid.dataset.scope = 'liquid';
    grids.push(grid);
    gridState.set(grid, { category: null, collection: null, sort: 'featured', filters: { availability: [], tagFilters: {} } });
  }

  function initGrids() {
    document.querySelectorAll('.blu-grid').forEach(grid => {
      registerGrid(grid);
    });
  }


/* ==========================================================
     5  UNIFIED FILTER + SORT STATE
     ========================================================== */
  // Map inline pill text to a product type
  const PILL_TYPE_MAP = {
    'view all':       null,
    't-shirts':       'tshirt',
    'hoodies':        'hoodie',
    'sherpa hoodies': 'hoodie',
    'jersey':         'jersey',
    'joggers':        'jogger',
    'polo':           'polo',
    'pants':          'pants',
    'cargo':          'pants',
    'caps':           'cap',
    'shirts':         'shirt',
    'knits':          'knit',
    'outerwear':      'outerwear',
  };

  // Categories offered in the Refine drawer
  const CATEGORY_OPTIONS = [
    { label: 'All',       type: null },
    { label: 'T-shirts',  type: 'tshirt' },
    { label: 'Hoodies',   type: 'hoodie' },
    { label: 'Shirts',    type: 'shirt' },
    { label: 'Pants',     type: 'pants' },
    { label: 'Joggers',   type: 'jogger' },
    { label: 'Knits',     type: 'knit' },
    { label: 'Caps',      type: 'cap' },
    { label: 'Polo',      type: 'polo' },
    { label: 'Jersey',    type: 'jersey' },
    { label: 'Outerwear', type: 'outerwear' },
  ];

  const SORT_OPTIONS = [
    { value: 'featured',   label: 'Featured' },
    { value: 'price-asc',  label: 'Price  Low to High' },
    { value: 'price-desc', label: 'Price  High to Low' },
    { value: 'newest',     label: 'Newest' },
  ];

  function animateCards(cards) {
    if (typeof gsap === 'undefined' || !cards.length) return;
    gsap.fromTo(cards,
      { opacity: 0, y: 18 },
      { opacity: 1, y: 0, duration: 0.6, stagger: 0.05, ease: 'power3.out', clearProps: 'opacity,transform' }
    );
  }

  // Apply a grid's {category, collection, sort} state: filter + reorder + animate
  function applyState(grid, animate) {
    const st = gridState.get(grid) || { category: null, collection: null, sort: 'featured', filters: { availability: [], tagFilters: {} } };
    const cards = Array.from(grid.querySelectorAll('.blu-card'));

    // Remove any existing empty state
    const existingEmpty = grid.querySelector('.blu-grid-empty');
    if (existingEmpty) existingEmpty.remove();

    // 1  filter (category + collection + advanced filters)
    cards.forEach(card => {
      let show = true;
      if (st.category && card.dataset.type !== st.category) show = false;
      if (st.collection && card.dataset.collection !== st.collection) show = false;
      
      // Handle advanced filters from new drawer
      if (st.filters && show) {
          // Dynamic tag-based filters (filter-GROUP:VALUE convention)
          if (st.filters.tagFilters) {
              const cardTags = (card.dataset.tags || '').toLowerCase();
              
              for (const [groupName, selectedValues] of Object.entries(st.filters.tagFilters)) {
                  if (selectedValues.length > 0 && show) {
                      const hasMatch = selectedValues.some(val => {
                          const tagToFind = 'filter-' + groupName + ':' + val;
                          return cardTags.includes(tagToFind.toLowerCase());
                      });
                      if (!hasMatch) show = false;
                  }
              }
          }
          
          // Availability (via data-inventory)
          if (show && st.filters.availability && st.filters.availability.length > 0) {
              if (st.filters.availability.includes('low-stock')) {
                  const inv = parseInt(card.dataset.inventory || '999');
                  if (inv > 8) show = false;
              }
              if (st.filters.availability.includes('in-stock')) {
                  const inv = parseInt(card.dataset.inventory || '0');
                  if (inv <= 0) show = false;
              }
          }
      }
      card.style.display = show ? '' : 'none';
    });

    // 2  sort (reorder DOM nodes)
    const ordered = cards.slice();
    if (st.sort === 'price-asc')       ordered.sort((a, b) => (+a.dataset.price) - (+b.dataset.price));
    else if (st.sort === 'price-desc') ordered.sort((a, b) => (+b.dataset.price) - (+a.dataset.price));
    else if (st.sort === 'newest')     ordered.sort((a, b) => (+b.dataset.new - +a.dataset.new) || (+a.dataset.index - +b.dataset.index));
    else                               ordered.sort((a, b) => (+a.dataset.index) - (+b.dataset.index));
    ordered.forEach(c => grid.appendChild(c));

    // 3  count visible products & update counter
    const visible = ordered.filter(c => c.style.display !== 'none');
    updateProductCount(grid, visible.length);

    // 4  show empty state if nothing visible
    if (visible.length === 0) {
      grid.insertAdjacentHTML('beforeend', '<div class="blu-grid-empty">No products match your current filters.</div>');
    }

    // 5  animate the visible set
    if (animate !== false) animateCards(visible);
  }

  // Update the product count badge for a grid
  function updateProductCount(grid, count) {
    const gridKey = grid.dataset.grid || grid.dataset.collection || '';
    const countEl = document.querySelector(`.blu-product-count[data-count-target="${gridKey}"]`);
    if (countEl) {
      countEl.textContent = count + (count === 1 ? ' product' : ' products');
    }
  }

  // Update the sort dropdown label text
  function updateSortLabel(grid, sortValue) {
    const gridKey = grid.dataset.grid || grid.dataset.collection || '';
    const trigger = document.querySelector(`.blu-sort-trigger[data-sort-for="${gridKey}"]`);
    if (!trigger) return;
    const SORT_LABELS = { featured: 'Featured', 'price-asc': 'Price: Low  High', 'price-desc': 'Price: High  Low', newest: 'Newest' };
    const labelSpan = trigger.querySelector('span');
    if (labelSpan) labelSpan.textContent = 'Sort by: ' + (SORT_LABELS[sortValue] || 'Featured');
    // Update active state in dropdown options
    trigger.querySelectorAll('.blu-sort-option').forEach(opt => {
      opt.classList.toggle('active', opt.dataset.sort === sortValue);
    });
  }

  /* ---- inline pill <-> grid helpers ---- */
  function gridFor(wrap) {
    const c = wrap.closest('main, body');
    return c ? c.querySelector('.blu-grid') : null;
  }
  function pillsWrapFor(grid) {
    const c = grid.closest('main, body');
    return c ? c.querySelector('.blu-pills-wrap') : null;
  }
  function syncPills(grid) {
    const st = gridState.get(grid);
    const wrap = pillsWrapFor(grid);
    if (!wrap) return;
    wrap.querySelectorAll('.blu-pill').forEach(pill => {
      const t = PILL_TYPE_MAP[pill.textContent.trim().toLowerCase()] || null;
      pill.classList.toggle('active', t === (st.category || null));
    });
  }

  function wireFilters() {
    document.querySelectorAll('.blu-pills-wrap').forEach(wrap => {
      const grid = gridFor(wrap);
      if (!grid) return;
      wrap.querySelectorAll('.blu-pill').forEach(pill => {
        pill.addEventListener('click', () => {
          const st = gridState.get(grid);
          st.category = PILL_TYPE_MAP[pill.textContent.trim().toLowerCase()] || null;
          gridState.set(grid, st);
          syncPills(grid);
          applyState(grid);
           // keep drawer in sync when it is open
        });
      });
      syncPills(grid); // mark the initial "View all"
    });
  }

  /* ==========================================================
     5c  INLINE SORT DROPDOWN
     ========================================================== */
  function wireSortDropdowns() {
    document.querySelectorAll('.blu-sort-trigger').forEach(trigger => {
      const gridKey = trigger.dataset.sortFor;
      const grid = document.querySelector(`.blu-grid[data-grid="${gridKey}"]`) ||
                   document.querySelector(`.blu-grid[data-collection="${gridKey}"]`);
      if (!grid) return;

      // Toggle dropdown open/close
      trigger.addEventListener('click', (e) => {
        // Don't toggle if clicking an option inside
        if (e.target.closest('.blu-sort-option')) return;
        e.stopPropagation();
        trigger.classList.toggle('is-open');
      });

      // Option clicks
      trigger.querySelectorAll('.blu-sort-option').forEach(opt => {
        opt.addEventListener('click', (e) => {
          e.stopPropagation();
          const st = gridState.get(grid);
          st.sort = opt.dataset.sort;
          gridState.set(grid, st);
          updateSortLabel(grid, st.sort);
          applyState(grid);
          
          trigger.classList.remove('is-open');
        });
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', () => {
      document.querySelectorAll('.blu-sort-trigger.is-open').forEach(t => t.classList.remove('is-open'));
    });
  }

  /* ==========================================================
     5b  REFINE DRAWER (sort / category / collection)
     ========================================================== */
  let drawerEl = null, overlayEl = null, drawerGrid = null;

  
  /* ==========================================================
     5b  NEW FILTER DRAWER (HTML driven)
     ========================================================== */
  function wireNewFilterDrawer() {
    const drawer = document.getElementById('filter-drawer');
    const closeBtn = document.getElementById('filter-close');
    const overlay = document.getElementById('filter-overlay');
    const applyBtn = document.getElementById('filter-apply');
    const clearBtn = document.getElementById('filter-clear');
    
    if (!drawer) return;
    
    function closeFilterDrawer() {
        drawer.classList.remove('open');
        document.body.style.overflow = '';
        if (window.lenis) window.lenis.start();
    }
    
    if (closeBtn) closeBtn.addEventListener('click', closeFilterDrawer);
    if (overlay) overlay.addEventListener('click', closeFilterDrawer);
    
    // Listen to Advance Filters button
    document.querySelectorAll('.blu-advance').forEach(btn => {
        btn.addEventListener('click', () => {
            drawer.classList.add('open');
            document.body.style.overflow = 'hidden';
            if (window.lenis) window.lenis.stop();
            drawerGrid = gridFor(btn);
        });
    });
    
    // Apply Filters (supports dynamic tag-based groups)
    if (applyBtn) {
        applyBtn.addEventListener('click', () => {
            if (!drawerGrid) drawerGrid = document.querySelector('.blu-grid');
            if (!drawerGrid) { closeFilterDrawer(); return; }
            
            const st = gridState.get(drawerGrid);
            if (!st.filters) st.filters = {};
            
            // Collect availability (hardcoded group)
            st.filters.availability = Array.from(drawer.querySelectorAll('input[name="availability"]:checked')).map(cb => cb.value);
            
            // Collect ALL dynamic tag-based filter groups (name="tag-GROUPNAME")
            st.filters.tagFilters = {};
            const allTagCheckboxes = drawer.querySelectorAll('input[type="checkbox"][name^="tag-"]:checked');
            allTagCheckboxes.forEach(cb => {
                const groupName = cb.name.replace('tag-', '');
                if (!st.filters.tagFilters[groupName]) st.filters.tagFilters[groupName] = [];
                st.filters.tagFilters[groupName].push(cb.value);
            });
            
            // Sort
            const sortRadio = drawer.querySelector('input[name="sort"]:checked');
            if (sortRadio && sortRadio.value !== 'default') {
                st.sort = sortRadio.value;
            } else {
                st.sort = 'featured';
            }
            
            gridState.set(drawerGrid, st);
            applyState(drawerGrid);
            closeFilterDrawer();
        });
    }
    
    // Clear Filters
    if (clearBtn) {
        clearBtn.addEventListener('click', () => {
            drawer.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(inp => {
                if (inp.type === 'radio' && inp.value === 'default') inp.checked = true;
                else inp.checked = false;
            });
            
            if (drawerGrid) {
                const st = gridState.get(drawerGrid);
                st.filters = { availability: [], tagFilters: {} };
                st.sort = 'featured';
                gridState.set(drawerGrid, st);
                applyState(drawerGrid);
            }
            closeFilterDrawer();
        });
    }
  }


  /* ==========================================================
     6  ADD-TO-BAG (delegated, text button)
     ========================================================== */
  function wireAddToBag() {
    document.addEventListener('click', e => {
      const btn = e.target.closest('.blu-card-add');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      if (btn.dataset.busy) return;
      btn.dataset.busy = '1';
      const original = btn.dataset.originalHTML || btn.innerHTML;
      btn.dataset.originalHTML = original;
      btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"></path></svg>`;
      btn.classList.add('is-added');
      const countEl = document.querySelector('.cart-count');
      if (countEl) countEl.textContent = (parseInt(countEl.textContent, 10) || 0) + 1;
      setTimeout(() => {
        btn.innerHTML = original;
        btn.classList.remove('is-added');
        delete btn.dataset.busy;
      }, 1400);
    });
  }

  /* ==========================================================
     7  SCROLL REVEAL (slow, editorial pacing)
     ========================================================== */
  function revealCards() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    grids.forEach(grid => {
      const cards = grid.querySelectorAll('.blu-card');
      if (!cards.length) return;
      gsap.fromTo(cards,
        { opacity: 0, y: 30 },
        {
          opacity: 1, y: 0,
          duration: 0.9, stagger: 0.08, ease: 'power3.out',
          scrollTrigger: { trigger: grid, start: 'top 85%' },
          clearProps: 'opacity,transform',
        }
      );
    });
  }

  /* ==========================================================
     BOOT
     ========================================================== */
    function initCatalog() {
    initGrids();
    wireNewFilterDrawer();
    wireFilters();
    wireSortDropdowns();
    wireAddToBag();

    grids.forEach(grid => {
      const cards = grid.querySelectorAll('.blu-card');
      const gridKey = grid.dataset.grid || grid.dataset.collection || '';
      updateProductCount(grid, cards.length);
    });

    if (document.readyState === 'complete') {
      revealCards();
    } else {
      window.addEventListener('load', revealCards);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCatalog);
  } else {
    initCatalog();
  }
  
  
})();



(function() {
  function initToggles() {
    const toggles = document.querySelectorAll('.grid-toggle-btn');
    toggles.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const col = e.currentTarget.getAttribute('data-col');
        toggles.forEach(t => t.classList.remove('active'));
        document.querySelectorAll(`.grid-toggle-btn[data-col="${col}"]`).forEach(t => t.classList.add('active'));
        document.querySelectorAll('.blu-grid').forEach(grid => {
          grid.classList.remove('is-1-col', 'is-2-col');
          grid.classList.add(`is-${col}-col`);
        });
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initToggles);
  } else {
    initToggles();
  }
})();
